import { useMemo } from 'react'
import { DataTableCell, DataTableHeader, StyledDataTable } from './DataTable.styled'
import classNames from 'classnames'
import { Column, Row, useFilters, useTable } from 'react-table'

export interface IDataTableProps<T extends object> {
  rowClass?: (row: Row<T>, index?: number) => string
  height?: number
  columns: ColumnWrapper<T>[]
  data: T[]
  className?: string
  striped?: boolean
  emptyText?: string
  showNoData?: number
}

export const DEFAULT_VISIBLE_ROWS = 10
export const HEADER_HEIGHT = 39
export const ROW_HEIGHT = 36.5
export const DEFAULT_TABLE_HEIGHT = DEFAULT_VISIBLE_ROWS * ROW_HEIGHT + HEADER_HEIGHT

type ColumnWrapper<T extends object = {}> = Column<T> & { visible?: boolean }

export function DataTable<T extends object>({
  columns,
  data,
  striped = true,
  height,
  emptyText,
  showNoData = data?.length,
  rowClass
}: IDataTableProps<T>) {
  // Use the state and functions returned from useTable to build your UI
  const filteredColumns = useMemo(() => (columns || []).filter((c) => c.visible === undefined || c.visible), [columns])
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns: filteredColumns,
      data
    },
    useFilters
  )

  // Render the UI for your table
  return (
    <>
      {!!showNoData && (
        <StyledDataTable striped={striped} maxHeight={height}>
          <div className="table-container">
            <table className="table table-hover table-sm" {...getTableProps()}>
              <thead className="table-head">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps()}>
                        <DataTableHeader>{column.render('Header')}</DataTableHeader>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="table-body" {...getTableBodyProps()}>
                {rows.map((row, i) => {
                  prepareRow(row)
                  return (
                    <tr className={classNames('table-row', rowClass?.(row, i))} {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return (
                          <td {...cell.getCellProps()}>
                            <DataTableCell>{cell.render('Cell')}</DataTableCell>
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </StyledDataTable>
      )}
    </>
  )
}
